import type { Transition } from "@uirouter/core";

import Sentry from "@similarweb/sentry-logger";

import {
    calcCampaignEntireDuration,
    extractConfigScrapeDuration,
    filterOpenModelsForUser,
    getDefaultModel,
    isAllPlatformsOptionEnabled,
} from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/LLMTrackerUtils";
import type {
    IAiTrackerCampaign,
    ITabData,
    EAvailableTabs,
} from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/types";
import { CampaignStatus, LLModel } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/types";
import type { transitionHookFn } from "app/routes/handlers/redirectFunctions/redirectPipe";
import DurationService from "app/services/DurationService";

import { llmTrackerCampaignsQueryClient } from "./llm-tracker-service/llmTrackerCampaignsQueryClient";
import { getAvailableTabsConfig, DEFAULT_TOPIC } from "./LLMConsts";

const FALLBACK_STATE = {
    state: "digitalsuite_llm_solutions_homepage",
};

export const onRedirectToLLMReport = (): transitionHookFn => {
    return async (transition: Transition) => {
        const toParams = transition.params();
        const fromParams = transition.params("from");

        try {
            const allCampaigns = await llmTrackerCampaignsQueryClient.getCampaigns();
            const selectedCampaign = await llmTrackerCampaignsQueryClient.getCampaignById(toParams?.campaignId);
            const nonDemoCampaigns = allCampaigns.filter((campaign: IAiTrackerCampaign) => !campaign.isDemo);

            if (!selectedCampaign || (!nonDemoCampaigns?.length && !selectedCampaign?.isDemo)) {
                return FALLBACK_STATE;
            }

            //validate model
            const toModel = toParams?.model as LLModel;
            const targetState = transition.to().name;
            const currentTab: EAvailableTabs = (
                Object.entries(getAvailableTabsConfig()) as [EAvailableTabs, ITabData][]
            ).find(([, config]) => config.stateId === targetState)?.[0];

            const isAllPlatforms = toModel === LLModel.All;
            const defaultModel = getDefaultModel(selectedCampaign, currentTab, true);
            const availableModels = filterOpenModelsForUser([toModel ?? defaultModel], selectedCampaign?.isDemo);
            const configs = selectedCampaign?.configs ?? {};

            if (
                !toModel ||
                (isAllPlatforms && !isAllPlatformsOptionEnabled(currentTab)) || //all-platforms not available
                !(toModel in configs) || //not available for campaign
                !availableModels.includes(toModel) || //not available for user
                (selectedCampaign.configs?.[toModel]?.status === CampaignStatus.Completed &&
                    (!configs?.[toModel]?.firstScrapeDate || !configs?.[toModel]?.lastScrapeDate))
            ) {
                if (toModel !== defaultModel) {
                    return {
                        params: {
                            ...toParams,
                            model: defaultModel,
                        },
                    };
                } else {
                    //prevent infinite loop - access denied
                    return FALLBACK_STATE;
                }
            }

            //validate duration
            const wasCampaignChanged = fromParams?.campaignId && fromParams.campaignId !== toParams.campaignId;
            const wasModelChanged = fromParams?.model && fromParams.model !== toModel;
            const isDurationManuallyChanged =
                toParams?.isDurationChanged === true || toParams?.isDurationChanged === "true";

            const isCurrDurationInvalidForModel = wasModelChanged && !isDurationManuallyChanged && toParams?.duration && (() => {
                const currentDuration = DurationService.getDurationData(toParams?.duration).raw;
                const toDuration = extractConfigScrapeDuration(selectedCampaign, toModel);
                return currentDuration.from.isBefore(toDuration.from, "day") ||
                    currentDuration.to.isAfter(toDuration.to, "day");
            })();

            const allDays = calcCampaignEntireDuration(selectedCampaign, toModel);
            const shouldReset =
                toParams?.duration !== allDays &&
                (wasCampaignChanged || (wasModelChanged && isCurrDurationInvalidForModel));

            if (!toParams?.duration || shouldReset) {
                return {
                    params: {
                        ...toParams,
                        duration: allDays,
                    },
                };
            }

            if (!toParams?.topic) {
                return {
                    params: {
                        ...toParams,
                        topic: DEFAULT_TOPIC,
                    },
                };
            }
        } catch (error) {
            Sentry.errorTracker({
                e: error,
                message: "Error in LLM report redirect",
                level: "error",
                params: {
                    extra: {
                        toParams,
                    },
                },
            });
            return null;
        }
    };
};
